import React, { useState } from 'react';
import { Button, Card, Layout, Result, Typography, message } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import api, { API_ROOT_URL } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import Brand from '../components/Brand';
import styles from './OAuthConsent.module.css';

const { Content } = Layout;
const { Paragraph, Text } = Typography;

/**
 * Approves an MCP client's connection request for the signed-in admin.
 *
 * The OAuth server sends staff here instead of showing its own password form,
 * so the admin panel stays the single place anyone types a password. If there
 * is no session, the route guard shows the normal login first and returns here
 * afterwards — from the person's side it is one login, not two.
 */
const OAuthConsent: React.FC = () => {
  const [params] = useSearchParams();
  const requestId = params.get('request_id') || '';
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [denied, setDenied] = useState<string | null>(null);

  const approve = async () => {
    setSubmitting(true);
    try {
      // Root URL, not the /api/v1 baseURL: the OAuth router is mounted at the
      // API root. Still through `api` so the request keeps the panel's JWT and
      // its refresh-on-401 retry.
      const { data } = await api.post(`${API_ROOT_URL}/oauth/login/admin/approve`, {
        request_id: requestId,
      });
      // The server hands back where to send the browser; it cannot redirect us
      // itself because this is a fetch, not a form post.
      window.location.href = data.redirect_url;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403) {
        setDenied('Цей акаунт не має доступу до адмінського MCP.');
      } else if (status === 400) {
        setDenied('Запит на підключення застарів. Почніть підключення у застосунку ще раз.');
      } else {
        message.error('Не вдалося підтвердити підключення. Спробуйте ще раз.');
      }
      setSubmitting(false);
    }
  };

  if (!requestId) {
    return (
      <Layout className={styles.layout}>
        <Content className={styles.content}>
          <Result
            status="warning"
            title="Немає запиту на підключення"
            subTitle="Відкрийте цю сторінку з застосунку, який ви підключаєте."
          />
        </Content>
      </Layout>
    );
  }

  if (denied) {
    return (
      <Layout className={styles.layout}>
        <Content className={styles.content}>
          <Result status="error" title="Підключення не підтверджено" subTitle={denied} />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className={styles.layout}>
      <Content className={styles.content}>
        <Card className={styles.card}>
          <div className={styles.header}>
            <Brand />
          </div>

          <div className={styles.icon}>
            <ApiOutlined />
          </div>

          <h1 className={styles.title}>Підключити застосунок?</h1>

          <Paragraph className={styles.lead}>
            Застосунок отримає доступ <Text strong>лише на читання</Text> до даних
            магазину: замовлення, клієнти, товари, залишки, статистика. Змінювати
            чи видаляти щось він не зможе.
          </Paragraph>

          <Paragraph className={styles.who}>
            Ви увійшли як <Text strong>{user?.username || user?.email}</Text>
          </Paragraph>

          <Button
            type="primary"
            size="large"
            block
            loading={submitting}
            onClick={approve}
          >
            Підключити
          </Button>

          <Paragraph className={styles.foot}>
            Кожен запит застосунку записується в журнал. Доступ зникає, щойно
            акаунт деактивують.
          </Paragraph>
        </Card>
      </Content>
    </Layout>
  );
};

export default OAuthConsent;
